import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { X, FileText } from 'lucide-react'
import GenericInput from '../../genericComponents/input/GenericInputComponent'
import IconsPath from '../../assets/icons'
import LanguageTranslator from '../../locals/LanguageTranslator'
import { dispatchHandler } from '../../helper/apiHelperFunctions'

const TASK_TYPE_OPTIONS = [{ taskType: 'Cost Approvals' }, { taskType: 'Sample Review' }, { taskType: 'Shipment Issues' }, { taskType: 'Mentions' }]

const NORMAL_WEIGHT_CLASS = '[&_input]:!font-normal [&_textarea]:!font-normal [&_.ant-picker-input>input]:!font-normal'

const CreateTaskModal = (props) => {
    const { closeModal, availableDocument } = props
    const fileInputRef = useRef(null)
    const [inputData, setInputData] = useState({})
    const [errorData, setErrorData] = useState({})
    const [attachedFile, setAttachedFile] = useState(null)

    const dispatch = useDispatch()
    const taskPrioritiesData = useSelector((state) => state?.taskManagerStore?.getTaskPrioritiesData)

    useEffect(() => {
        if (!taskPrioritiesData?.isSuccess) {
            dispatchHandler(dispatch, "getTaskPrioritiesRequest")
        }
    }, [dispatch])

    const priorityDropdownOptions = (taskPrioritiesData?.data?.resource || []).map((priority) => ({ priority }))

    const DROPDOWN_FIELDS = [
        { key: 'taskType', label: 'Task Type', getListData: () => TASK_TYPE_OPTIONS },
        { key: 'entity', label: 'Entity', getListData: () => [] },
        { key: 'entityNumber', label: 'Entity Number', getListData: () => [] },
        { key: 'department', label: 'Department', getListData: () => [] },
        { key: 'assignTo', label: 'Assign To', getListData: () => [] },
        { key: 'priority', label: 'Priority', getListData: () => priorityDropdownOptions },
    ]

    const updateField = (key, value) => setInputData((prev) => ({ ...prev, [key]: value }))

    const handleFileChange = (e) => {
        const file = e.target.files?.[0]
        if (file) setAttachedFile(file)
    }

    const getDropdownValue = (key) => {
        const value = inputData?.[key]
        return typeof value === 'object' && value !== null ? value?.[key] || '' : value || ''
    }

    return (
        <div className="w-full h-full flex flex-col bg-white overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-borderSlateGrey shrink-0">
                <p className="mb-0 text-[16px] font-semibold text-fontBlack">
                    <LanguageTranslator>Creating New Task</LanguageTranslator>
                </p>
                <div className="flex items-center gap-3">
                    <span className="text-xs text-fontSlateGrey">Esc</span>
                    <span className="h-4 w-px bg-borderSlateGrey" />
                    <button type="button" onClick={closeModal} className="text-fontSlateGrey hover:text-fontBlack">
                        <X size={18} />
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
                <div className="grid grid-cols-2 gap-x-6">
                    <GenericInput
                        availableDocument={availableDocument}
                        type="text"
                        inputKey="title"
                        label="Title"
                        placeholder="Text here"
                        gibInnerClassName={NORMAL_WEIGHT_CLASS}
                        value={inputData?.title || ''}
                        onChange={(e) => updateField('title', e.target.value)}
                        errorData={errorData}
                        setErrorData={setErrorData}
                    />

                    {DROPDOWN_FIELDS.map((field) => (
                        <GenericInput
                            key={field.key}
                            availableDocument={availableDocument}
                            type="dropdown"
                            inputKey={field.key}
                            label={field.label}
                            placeholder="Select"
                            iconRight={<IconsPath.DownArrowNew />}
                            gibInnerClassName={NORMAL_WEIGHT_CLASS}
                            value={getDropdownValue(field.key)}
                            actualInputValue={inputData?.[field.key]}
                            inputProps={() => ({
                                headerKeys: { [field.key]: { value: '' } },
                                itemKeys: { [field.key]: [field.key] },
                                getListData: field.getListData,
                            })}
                            onChange={(data) => updateField(field.key, data)}
                            errorData={errorData}
                            setErrorData={setErrorData}
                        />
                    ))}

                    <GenericInput
                        availableDocument={availableDocument}
                        type="date"
                        inputKey="dueDate"
                        label="Due Date"
                        placeholder="Select"
                        gibInnerClassName={NORMAL_WEIGHT_CLASS}
                        value={inputData?.dueDate}
                        onChange={(value) => updateField('dueDate', value)}
                        errorData={errorData}
                        setErrorData={setErrorData}
                    />

                    <div className="col-span-2 mb-[10px]">
                        <label className="mb-1.5 block text-[11px] font-semibold text-fontSlateGrey">
                            <LanguageTranslator>Attachment</LanguageTranslator>
                        </label>
                        <div
                            onClick={() => fileInputRef.current?.click()}
                            className="flex flex-col items-center justify-center gap-2 py-8 rounded-[8px] border border-dashed border-borderSlateGrey bg-[#FAFBFC] cursor-pointer"
                        >
                            <FileText size={28} className="text-fontSlateGrey" />
                            <p className="mb-0 text-[13px] text-fontBlack">
                                <LanguageTranslator>Drag & Drop an document file, or</LanguageTranslator>{' '}
                                <span className="text-primary font-semibold">
                                    <LanguageTranslator>Browse</LanguageTranslator>
                                </span>
                            </p>
                            <p className="mb-0 text-xs text-fontSlateGrey">Max file size : 10MB&nbsp;&nbsp;|&nbsp;&nbsp;Supported: PDF, XLSX, CSV, JPEG, PNG</p>
                            {attachedFile && <p className="mb-0 text-xs text-primary">{attachedFile.name}</p>}
                            <input
                                ref={fileInputRef}
                                type="file"
                                className="hidden"
                                accept=".pdf,.xlsx,.csv,.jpeg,.jpg,.png"
                                onChange={handleFileChange}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    </div>

                    <GenericInput
                        availableDocument={availableDocument}
                        type="textarea"
                        inputKey="description"
                        label="Description"
                        placeholder="Text here"
                        genInputExtraClass="col-span-2"
                        gibInnerClassName={`!h-auto ${NORMAL_WEIGHT_CLASS}`}
                        rows={3}
                        value={inputData?.description || ''}
                        onChange={(e) => updateField('description', e.target.value)}
                        errorData={errorData}
                        setErrorData={setErrorData}
                    />
                </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-borderSlateGrey shrink-0">
                <button
                    type="button"
                    onClick={closeModal}
                    className="text-[12px] font-medium px-4 py-2 rounded-[4px] border border-borderSlateGrey text-fontBlack hover:bg-[#F8FAFC]"
                >
                    <LanguageTranslator>Cancel</LanguageTranslator>
                </button>
                <button type="button" className="text-[12px] font-medium px-4 py-2 rounded-[4px] bg-primary text-white hover:opacity-90">
                    <LanguageTranslator>Create Task</LanguageTranslator>
                </button>
            </div>
        </div>
    )
}

export default CreateTaskModal
